import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, ChevronRight, Loader2 } from 'lucide-react';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import {
  useTranslation,
  type OfferCampaignSellableOption,
  type OfferCampaignWithItems,
  type TranslationKey,
} from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useNavBlocker, useNavBlockerMarkSettled } from '@/hooks/use-nav-blocker';
import {
  createOfferCampaignFormSchema,
  type OfferCampaignFormValues,
} from '../lib/offer-campaign-form-schema';
import { OfferCampaignSellablePicker } from './offer-campaign-sellable-picker';

export interface OfferCampaignFormSubmit {
  readonly title: string;
  readonly short_description: string | null;
  readonly starts_at: string;
  readonly ends_at: string;
  readonly is_active: boolean;
  readonly variant_ids: readonly string[];
}

export interface OfferCampaignFormPageProps {
  readonly mode: 'create' | 'edit';
  readonly storeId: string | null;
  readonly campaign?: OfferCampaignWithItems | null;
  readonly isSubmitting?: boolean;
  readonly errorKey?: TranslationKey | null;
  readonly onSubmit: (values: OfferCampaignFormSubmit) => Promise<void>;
  readonly onCancel: () => void;
}

function toDateTimeLocal(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function defaultStart(): string {
  const date = new Date();
  date.setMinutes(date.getMinutes() + 30);
  date.setSeconds(0, 0);
  return toDateTimeLocal(date.toISOString());
}

function defaultEnd(start: string): string {
  const date = new Date(start);
  if (!Number.isFinite(date.getTime())) return '';
  date.setHours(date.getHours() + 24);
  return toDateTimeLocal(date.toISOString());
}

function toIsoFromDateTimeLocal(value: string): string {
  return new Date(value).toISOString();
}

function toInitialSellableOptions(
  campaign: OfferCampaignWithItems | null | undefined,
): OfferCampaignSellableOption[] {
  return (campaign?.items ?? []).map((item) => ({
    product_id: item.product_id,
    variant_id: item.variant_id,
    product_name: item.product_name,
    category_id: null,
    category_name: item.category_name,
    option_summary: item.variant_option_summary,
    is_default_variant: item.is_default_variant,
    price_cents: item.variant_price_cents,
    sale_price_cents: item.variant_sale_price_cents ?? 0,
    currency: item.currency,
    available: item.available,
    active: item.active,
    product_published: item.product_published,
    availability_state: item.available > 0 ? 'available' : 'out_of_stock',
    isSelectable:
      item.active &&
      item.product_published &&
      item.available > 0 &&
      item.variant_sale_price_cents !== null &&
      item.variant_sale_price_cents > 0 &&
      item.variant_sale_price_cents < item.variant_price_cents,
  }));
}

export function OfferCampaignFormPage({
  mode,
  storeId,
  campaign,
  isSubmitting = false,
  errorKey,
  onSubmit,
  onCancel,
}: OfferCampaignFormPageProps) {
  const { t, dir } = useTranslation();
  const schema = useMemo(() => createOfferCampaignFormSchema(), []);
  const startsAt = toDateTimeLocal(campaign?.starts_at) || defaultStart();
  const defaultValues = useMemo<OfferCampaignFormValues>(() => {
    const start = toDateTimeLocal(campaign?.starts_at) || startsAt;
    return {
      title: campaign?.title ?? '',
      short_description: campaign?.short_description ?? '',
      starts_at: start,
      ends_at: toDateTimeLocal(campaign?.ends_at) || defaultEnd(start),
      is_active: campaign?.is_active ?? true,
      variant_ids: campaign?.items.map((item) => item.variant_id) ?? [],
    };
  }, [campaign, startsAt]);
  const initialSelectedOptions = useMemo(() => toInitialSellableOptions(campaign), [campaign]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty },
  } = useForm<OfferCampaignFormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues,
  });

  const markSettled = useNavBlockerMarkSettled();
  useNavBlocker(isDirty);

  const submitLabelKey: TranslationKey =
    mode === 'create'
      ? 'admin.offerCampaigns.form.submitCreate'
      : 'admin.offerCampaigns.form.submitEdit';
  const pageTitleKey: TranslationKey =
    mode === 'create'
      ? 'admin.offerCampaigns.form.pageTitleCreate'
      : 'admin.offerCampaigns.form.pageTitleEdit';

  const submit = handleSubmit(async (values) => {
    markSettled();
    await onSubmit({
      title: values.title.trim(),
      short_description:
        values.short_description.trim().length > 0 ? values.short_description.trim() : null,
      starts_at: toIsoFromDateTimeLocal(values.starts_at),
      ends_at: toIsoFromDateTimeLocal(values.ends_at),
      is_active: values.is_active,
      variant_ids: values.variant_ids,
    });
  });

  return (
    <form
      noValidate
      dir={dir}
      data-testid="offer-campaign-form-page"
      className="mx-auto max-w-screen-md px-6 pb-32 pt-8"
      aria-busy={isSubmitting}
      onSubmit={submit}
    >
      <Link
        to="/promotions/campaigns"
        className="text-zaatar mb-6 inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
        data-testid="offer-campaign-form-back"
      >
        <ChevronRight size={16} className="rtl:rotate-180" aria-hidden strokeWidth={2} />
        {t('admin.offerCampaigns.form.back' as TranslationKey)}
      </Link>

      <h1
        className="font-display text-foreground mb-8 text-start text-3xl tracking-tight"
        data-testid="offer-campaign-form-title"
      >
        {t(pageTitleKey)}
      </h1>

      <div className="border-border bg-card shadow-admin-card space-y-6 rounded-lg border p-6 sm:p-8">
        {errorKey ? (
          <div
            role="alert"
            data-testid="offer-campaign-form-submit-error"
            className="bg-destructive/10 border-destructive/25 text-destructive flex items-start gap-2 rounded-md border px-4 py-3 text-start text-sm"
          >
            <AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden />
            <span>{t(errorKey)}</span>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="offer-campaign-title" className="mb-1.5 block text-start">
              {t('admin.offerCampaigns.form.titleLabel' as TranslationKey)}
            </Label>
            <Input
              id="offer-campaign-title"
              type="text"
              dir="auto"
              maxLength={100}
              autoComplete="off"
              disabled={isSubmitting}
              placeholder={t('admin.offerCampaigns.form.titlePlaceholder' as TranslationKey)}
              aria-invalid={!!errors.title}
              aria-describedby={errors.title ? 'offer-campaign-title-error' : undefined}
              data-testid="offer-campaign-title-input"
              {...register('title')}
            />
            {errors.title ? (
              <p
                id="offer-campaign-title-error"
                role="alert"
                className="text-destructive mt-1 text-start text-xs"
              >
                {t(errors.title.message as TranslationKey)}
              </p>
            ) : null}
          </div>

          <label className="border-border bg-admin-surface-muted flex items-start gap-3 rounded-lg border p-3 text-start">
            <input
              type="checkbox"
              className="accent-admin-lime mt-1 size-4"
              disabled={isSubmitting}
              data-testid="offer-campaign-active-checkbox"
              {...register('is_active')}
            />
            <span className="text-foreground block text-sm font-medium">
              {t('admin.offerCampaigns.form.activeLabel' as TranslationKey)}
            </span>
          </label>
        </div>

        <div>
          <Label htmlFor="offer-campaign-description" className="mb-1.5 block text-start">
            {t('admin.offerCampaigns.form.descriptionLabel' as TranslationKey)}
          </Label>
          <Textarea
            id="offer-campaign-description"
            dir="auto"
            maxLength={240}
            disabled={isSubmitting}
            placeholder={t('admin.offerCampaigns.form.descriptionPlaceholder' as TranslationKey)}
            aria-invalid={!!errors.short_description}
            aria-describedby={
              errors.short_description ? 'offer-campaign-description-error' : undefined
            }
            data-testid="offer-campaign-description-input"
            {...register('short_description')}
          />
          {errors.short_description ? (
            <p
              id="offer-campaign-description-error"
              role="alert"
              className="text-destructive mt-1 text-start text-xs"
            >
              {t(errors.short_description.message as TranslationKey)}
            </p>
          ) : null}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="offer-campaign-starts-at" className="mb-1.5 block text-start">
              {t('admin.offerCampaigns.form.startsAtLabel' as TranslationKey)}
            </Label>
            <Input
              id="offer-campaign-starts-at"
              type="datetime-local"
              disabled={isSubmitting}
              aria-invalid={!!errors.starts_at}
              data-testid="offer-campaign-starts-at-input"
              {...register('starts_at')}
            />
          </div>
          <div>
            <Label htmlFor="offer-campaign-ends-at" className="mb-1.5 block text-start">
              {t('admin.offerCampaigns.form.endsAtLabel' as TranslationKey)}
            </Label>
            <Input
              id="offer-campaign-ends-at"
              type="datetime-local"
              disabled={isSubmitting}
              aria-invalid={!!errors.ends_at}
              aria-describedby={errors.ends_at ? 'offer-campaign-window-error' : undefined}
              data-testid="offer-campaign-ends-at-input"
              {...register('ends_at')}
            />
            {errors.ends_at ? (
              <p
                id="offer-campaign-window-error"
                role="alert"
                className="text-destructive mt-1 text-start text-xs"
              >
                {t(errors.ends_at.message as TranslationKey)}
              </p>
            ) : null}
          </div>
        </div>

        <Controller
          control={control}
          name="variant_ids"
          render={({ field }) => (
            <OfferCampaignSellablePicker
              storeId={storeId}
              selectedVariantIds={field.value}
              initialSelectedOptions={initialSelectedOptions}
              disabled={isSubmitting}
              errorKey={(errors.variant_ids?.message as TranslationKey | undefined) ?? null}
              onChange={(next) => field.onChange(next)}
            />
          )}
        />
      </div>

      <div className="bg-background/95 border-border supports-[backdrop-filter]:bg-background/80 fixed inset-x-0 bottom-0 z-20 border-t px-6 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-screen-md flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="ghost" disabled={isSubmitting} onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting} aria-busy={isSubmitting || undefined}>
            {isSubmitting ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            <span>{t(submitLabelKey)}</span>
          </Button>
        </div>
      </div>
    </form>
  );
}
